import React from "react";
import {
  Card,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Scrollbar } from "src/components/scrollbar";
import { useThumbzApi } from "src/services/thumbz-api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "src/config/query-key";
import toast from "react-hot-toast";
import { Restore, MoreVertOutlined } from "@mui/icons-material";
import { useConfirm } from "material-ui-confirm";
import { useAppSelector } from "src/app/hooks";
import { organizationSlice } from "src/features/organization/organizationSlice";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale/pt-BR";

interface WorkspaceArchivedProps {
  wrk_id: string;
  org_id: string;
}

interface Approval {
  apv_id: string;
  apv_name: string;
  apv_created_at: string;
  apv_updated_at: string;
  apv_archived: boolean;
  wrk_id: string;
  wrk_name: string;
}

export const WorkspaceArchived: React.FC<WorkspaceArchivedProps> = ({ wrk_id }) => {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { api } = useThumbzApi();

  const { org_id } = useAppSelector(organizationSlice.selectors.selectedOrganization);

  const unarchiveApprovalMutation = useMutation({
    mutationFn: (apv_id: string) => api.approval.approvalControllerUnarchiveApproval({ apv_id }),
    onSuccess: () => {
      toast.success("Aprovação desarquivada com sucesso");
      queryClient.invalidateQueries(queryKeys.approval.list({ wrk_id, archived: false }));
      queryClient.invalidateQueries(queryKeys.approval.list({ wrk_id, archived: true }));
    },
    onError: (error, apv_id) => {
      console.error(`❌ Erro ao desarquivar aprovação: ${apv_id}`, error);
      toast.error("Erro ao desarquivar aprovação. Tente novamente.");
    },
  });

  const approvalsQuery = useQuery({
    queryKey: queryKeys.approval.list({ wrk_id, archived: true }).queryKey,
    queryFn: async () => {
      try {
        const response = await api.approval.approvalControllerList({
          wrk_id,
          org_id,
          archived: true,
        });
        return Array.isArray(response) ? response : [];
      } catch (error) {
        toast.error("Erro ao carregar aprovações arquivadas. Tente novamente.");
        return [];
      }
    },
  });

  const formatDateToLocalTimezone = (date: string | Date, formatStr: string): string => {
    if (!date) return "Invalid Date";
    const utcDate = new Date(date);
    const saoPauloDate = new Date(utcDate.getTime() - 3 * 60 * 60 * 1000);
    return format(saoPauloDate, formatStr, { locale: ptBR });
  };

  const handleUnarchive = (event: React.MouseEvent<HTMLElement>, apv_id: string) => {
    event.stopPropagation();

    confirm({
      title: "Desarquivar?",
      description: `Tem certeza que deseja desarquivar esta aprovação?`,
      confirmationText: "Sim, desarquivar",
      cancellationText: "Cancelar",
      confirmationButtonProps: { color: "primary" },
    }).then(() => {
      unarchiveApprovalMutation.mutate(apv_id);
    });
  };

  const rowData: Approval[] =
    approvalsQuery.data?.map((approval) => ({
      apv_id: approval.apv_id,
      apv_name: approval.apv_name,
      apv_created_at: formatDateToLocalTimezone(approval.apv_created_at, "dd/MM/yyyy HH:mm"),
      apv_updated_at: approval.apv_updated_at
        ? formatDateToLocalTimezone(approval.apv_updated_at, "dd/MM/yyyy HH:mm")
        : "Sem atualização",
      apv_archived: approval.apv_archived ?? false,
      wrk_id: approval.wrk_id ?? "Desconhecido",
      wrk_name: approval.wrk_name ?? "Sem Workspace",
    })) ?? [];

  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, apv_id: string) => {
    event.stopPropagation();
    setSelectedId(apv_id);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedId(null);
  };

  return (
    <Stack spacing={2}>
      <Card>
        <Scrollbar>
          <Table aria-label="Tabela de aprovações arquivadas" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell scope="col">Nome da Aprovação</TableCell>
                <TableCell scope="col">Data Criação</TableCell>
                <TableCell scope="col">Última Atualização</TableCell>
                <TableCell scope="col">Nome do Workspace</TableCell>
                <TableCell scope="col" align="right">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Sem linhas para mostrar
                  </TableCell>
                </TableRow>
              ) : (
                rowData.map((row) => (
                  <TableRow key={row.apv_id} hover>
                    <TableCell>{row.apv_name}</TableCell>
                    <TableCell>{row.apv_created_at}</TableCell>
                    <TableCell>{row.apv_updated_at}</TableCell>
                    <TableCell>{row.wrk_name}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, row.apv_id)}>
                        <MoreVertOutlined />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Scrollbar>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
          <MenuItem
            onClick={(event) => {
              if (selectedId) {
                handleUnarchive(event, selectedId);
              }
              handleMenuClose();
            }}
            disabled={unarchiveApprovalMutation.isPending}
          >
            <ListItemIcon>
              <Restore fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {unarchiveApprovalMutation.isPending ? "Desarquivando..." : "Desarquivar"}
            </ListItemText>
          </MenuItem>
        </Menu>
      </Card>
    </Stack>
  );
};
